import { useState } from 'react';
import { toast } from 'react-toastify';

import {
  SitrepSectionType,
  useRemoveSitrepSectionV2,
  useUpdateSitrepSectionV2,
  useMoveSitrepSectionV2,
} from 'components/DisasterGPT/hooks/useSitrepsV2';
import Sentry from '../../../instrument';

/**
 * Encapsulates section CRUD operations (save settings, delete, move) and the
 * edit-settings-drawer state for a sitrep card.
 */
const useSitrepSectionActions = (
  sitrepId: UUID,
  sections: SitrepSectionType[] | undefined,
  refetch: () => void
) => {
  const [editSettingsSection, setEditSettingsSection] =
    useState<SitrepSectionType | null>(null);

  const removeSectionMutation = useRemoveSitrepSectionV2();
  const updateSectionMutation = useUpdateSitrepSectionV2();
  const moveSectionMutation = useMoveSitrepSectionV2();

  const handleSaveSettings = async (formData: {
    id: string;
    name: string;
    type: string;
    data_sources: string[];
    ai_instructions: string;
    output_format_structured: { summary_options: string[] };
  }) => {
    if (!editSettingsSection) return;

    try {
      await updateSectionMutation.mutateAsync({
        sitrepId,
        sectionId: editSettingsSection.id,
        updatedFields: {
          name: formData.name,
          type: formData.type,
          data_sources: formData.data_sources,
          ai_instructions: formData.ai_instructions,
          output_format_structured: formData.output_format_structured,
        },
      });
      setEditSettingsSection(null);
      await refetch();
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  const handleDeleteSection = async (section: SitrepSectionType) => {
    try {
      await removeSectionMutation.mutateAsync({
        sitrepId,
        sectionId: section.id,
      });
      await refetch();
      toast.success('Section deleted');
    } catch (error) {
      Sentry.captureException(error);
      toast.error('Failed to delete section');
    }
  };

  const handleMoveSection = async (direction: 'up' | 'down') => {
    if (!editSettingsSection) return;
    try {
      await moveSectionMutation.mutateAsync({
        sitrepId,
        sectionId: editSettingsSection.id,
        direction,
      });
      await refetch();
      toast.success(`Section moved ${direction}`);
    } catch (error) {
      Sentry.captureException(error);
      toast.error('Failed to move section');
    }
  };

  // Calculate section position for move button states
  const sectionIndex = (sections ?? []).findIndex(
    (s) => s.id === editSettingsSection?.id
  );
  const canMoveUp = sectionIndex > 0;
  const canMoveDown =
    sectionIndex >= 0 && sectionIndex < (sections?.length ?? 0) - 1;

  return {
    editSettingsSection,
    setEditSettingsSection,
    handleSaveSettings,
    handleDeleteSection,
    handleMoveSection,
    canMoveUp,
    canMoveDown,
  } as const;
};

export default useSitrepSectionActions;
